<template>
  <div class="daily-report">
    <h1>Reporte Diario</h1>

    <div class="date-selector">
      <label for="reportDate">Seleccionar Fecha:</label>
      <input type="date" id="reportDate" v-model="selectedDate" @change="fetchDailyReport">
    </div>

    <p>Fecha: {{ formattedDate }}</p>
    <p>Total Recaudado: ${{ report.total ? report.total.toFixed(2) : '0.00' }}</p>
    <p>Servicios Realizados: {{ report.servicios || 0 }}</p>
    <p>Reportes Generados: {{ report.reportes || 0 }}</p>

    <h2>Reporte por Manicurista</h2>
    <table class="manicurista-report-table">
      <thead>
        <tr>
          <th>Manicurista</th>
          <th>Atenciones</th>
          <th>Pagos</th>
          <th>Diferencia</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="manicurista in manicuristaPerformance" :key="manicurista.manicurista_id"
            :class="{ 'mismatch-row': manicurista.total_attentions !== manicurista.total_payments_count }">
          <td>{{ manicurista.manicurista_nombre }}</td>
          <td>{{ manicurista.total_attentions }}</td>
          <td>{{ manicurista.total_payments_count }}</td> <!-- Display count -->
          <td>{{ manicurista.total_attentions - manicurista.total_payments_count }}</td> <!-- Calculate difference with count -->
        </tr>
        <tr v-if="manicuristaPerformance.length === 0">
          <td colspan="4">No hay datos para la fecha seleccionada.</td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  name: 'DailyReport',
  data() {
    return {
      selectedDate: new Date().toLocaleDateString('en-CA'), // YYYY-MM-DD format for input type="date"
      report: {
        total: 0,
        servicios: 0,
        reportes: 0,
      },
      manicuristaPerformance: [],
    };
  },
  computed: {
    formattedDate() {
      // Parse the YYYY-MM-DD string manually to avoid timezone issues
      const [year, month, day] = this.selectedDate.split('-').map(Number);
      // Month is 0-indexed in JavaScript Date (0 for January, 11 for December)
      const date = new Date(year, month - 1, day); 
      return date.toLocaleDateString();
    },
  },
  created() {
    this.fetchDailyReport();
  },
  methods: {
    async fetchDailyReport() {
      try {
        // Fetch aggregated report data
        const reportResponse = await axios.get('/api/daily-report', {
          params: { date: this.selectedDate }
        });
        this.report = reportResponse.data;

        // Fetch attentions by manicurist
        const attentionsResponse = await axios.get('/api/daily-attentions-by-manicurist', {
          params: { date: this.selectedDate }
        });
        const attentionsData = attentionsResponse.data;

        // Fetch payments by manicurist
        const paymentsResponse = await axios.get('/api/daily-payments-by-manicurist', {
          params: { date: this.selectedDate }
        });
        const paymentsData = paymentsResponse.data;

        // Combine data
        const performanceMap = {};

        // Populate with all manicuristas from attentions (they might not have payments)
        attentionsData.forEach(manicurista => {
          performanceMap[manicurista.manicurista_id] = {
            manicurista_id: manicurista.manicurista_id,
            manicurista_nombre: manicurista.manicurista_nombre,
            total_attentions: manicurista.total_attentions,
            total_payments: 0, // Initialize to 0
          };
        });

        // Update with payments data (manicuristas might have payments but no attentions)
        paymentsData.forEach(manicurista => {
          const paymentsCount = manicurista.payments ? manicurista.payments.length : 0; // Get count of payments
          if (!performanceMap[manicurista.manicurista_id]) {
            performanceMap[manicurista.manicurista_id] = {
              manicurista_id: manicurista.manicurista_id,
              manicurista_nombre: manicurista.manicurista_nombre,
              total_attentions: 0, // Initialize to 0
              total_payments_count: paymentsCount, // Store count of payments
              total_payments_amount: manicurista.total_payments, // Store total amount for future use if needed
            };
          } else {
            performanceMap[manicurista.manicurista_id].total_payments_count = paymentsCount; // Store count of payments
            performanceMap[manicurista.manicurista_id].total_payments_amount = manicurista.total_payments; // Store total amount
          }
        });

        this.manicuristaPerformance = Object.values(performanceMap);

      } catch (error) {
        console.error('Error fetching daily report data:', error);
      }
    },
  },
};
</script>

<style scoped>
.daily-report {
  max-width: 800px; /* Increased max-width */
  margin: 0 auto;
  padding: 20px;
  border: 1px solid #eee;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

p {
  font-size: 1.1em;
  margin-bottom: 10px;
}

.date-selector {
  margin-bottom: 20px;
}

.date-selector label {
  margin-right: 10px;
  font-weight: bold;
}

.date-selector input[type="date"] {
  padding: 8px;
  border-radius: 4px;
  border: 1px solid #ddd;
}

h2 {
  margin-top: 30px;
  margin-bottom: 15px;
  color: #333;
}

.manicurista-report-table {
  width: 100%;
  border-collapse: collapse;
  margin-top: 15px;
}

.manicurista-report-table th,
.manicurista-report-table td {
  border: 1px solid #ddd;
  padding: 10px;
  text-align: left;
}

.manicurista-report-table th {
  background-color: #f2f2f2;
  font-weight: bold;
}

.mismatch-row {
  background-color: #ffe0e0; /* Light red for mismatch */
}
</style>
